// Theme: light | dark. Persisted in localStorage, applied as a `data-theme`
// attribute on <html>. Light is the default — the warm-cream palette in
// docs/style.md is the canonical look; dark is opt-in.
//
// "system" was supported in an earlier iteration but never matched the
// mockups (which only showed light + dark). On read we now coerce any
// non-"dark" stored value back to "light" so legacy localStorage entries
// don't pin a user to the wrong default.

export type Theme = "light" | "dark";

const STORAGE_KEY = "ophelia.theme";
const THEME_EVENT = "ophelia:theme-change";

export function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "dark" ? "dark" : "light";
}

export function setStoredTheme(theme: Theme): void {
  window.localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
  window.dispatchEvent(new CustomEvent<Theme>(THEME_EVENT, { detail: theme }));
}

/** Resolve a theme choice to the concrete value to apply. Kept as a function
 * (returning Theme directly) so the call sites that used to handle "system"
 * keep compiling without behaviour change. */
export function resolveTheme(theme: Theme): Theme {
  return theme;
}

export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

export function onStoredThemeChange(handler: (theme: Theme) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<Theme>).detail);
  window.addEventListener(THEME_EVENT, listener);
  return () => window.removeEventListener(THEME_EVENT, listener);
}

/** No-op kept around so any straggler imports don't break compilation.
 * "system" mode is no longer supported; we never need to react to OS-level
 * `prefers-color-scheme` changes. */
export function watchSystemTheme(_onChange: () => void): () => void {
  return () => {};
}
